/**
 * CartIcon Component
 * 
 * Shopping cart icon with item count badge that triggers the cart dropdown/sidebar.
 * Provides visual feedback for cart state and integrates with the shopping cart system.
 * 
 * Features:
 * - Animated cart icon with item count badge
 * - Visual feedback for cart state changes
 * - Responsive design for mobile and desktop
 * - Accessibility features
 * - Loading state indication
 * - Error state handling
 */

import React from 'react';
import { useShoppingCart } from '../../hooks/useShoppingCart';

// Component props interface
interface CartIconProps {
  onClick: () => void;
  className?: string;
  showLabel?: boolean;
  variant?: 'default' | 'minimal' | 'button';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * CartIcon component for triggering shopping cart
 */
export const CartIcon: React.FC<CartIconProps> = ({
  onClick,
  className = '',
  showLabel = false,
  variant = 'default',
  size = 'md',
}) => {
  const { itemCount, isLoading, error } = useShoppingCart();

  // Size classes
  const sizeClasses = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  // Badge size classes
  const badgeSizeClasses = {
    sm: 'w-4 h-4 text-xs',
    md: 'w-5 h-5 text-xs',
    lg: 'w-6 h-6 text-sm',
  };

  // Variant classes
  const variantClasses = {
    default: 'text-gray-300 hover:text-white transition-colors',
    minimal: 'text-gray-400 hover:text-gray-200 transition-colors',
    button: 'bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors border border-gray-600',
  };

  const iconSize = sizeClasses[size];
  const badgeSize = badgeSizeClasses[size];
  const variantClass = variantClasses[variant];

  return (
    <button
      onClick={onClick}
      className={`relative inline-flex items-center space-x-2 ${variantClass} ${className}`}
      aria-label={`Shopping cart with ${itemCount} items`}
      disabled={isLoading}
    >
      {/* Cart Icon */}
      <div className="relative">
        <svg
          className={`${iconSize} ${isLoading ? 'animate-pulse' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h8m-8 0a2 2 0 100 4 2 2 0 000-4zm8 0a2 2 0 100 4 2 2 0 000-4z"
          />
        </svg>

        {/* Item Count Badge */}
        {itemCount > 0 && (
          <span
            className={`absolute -top-2 -right-2 ${badgeSize} bg-red-600 text-white rounded-full flex items-center justify-center font-medium animate-pulse`}
            aria-label={`${itemCount} items in cart`}
          >
            {itemCount > 99 ? '99+' : itemCount}
          </span>
        )}

        {/* Error Indicator */}
        {error && (
          <span
            className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-500 rounded-full animate-pulse"
            title="Cart error - click to view"
            aria-label="Cart has errors"
          />
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping" />
          </div>
        )}
      </div>

      {/* Optional Label */}
      {showLabel && (
        <span className="text-sm font-medium">
          Cart
          {itemCount > 0 && ` (${itemCount})`}
        </span>
      )}
    </button>
  );
};

export default CartIcon;
